/**
 * Theme Slice
 *
 * Manages dark mode state with persistence to localStorage and server.
 * Applies the 'dark' class to document.documentElement when enabled.
 *
 * @validates Dark Mode Implementation
 */

import type { StateCreator } from 'zustand';
import type { UserPreferences } from '@spellion/shared';

const THEME_STORAGE_KEY = 'spellion_theme';
const API_BASE_URL = import.meta.env.VITE_API_URL || '/api';

// ============================================================================
// Theme State Types
// ============================================================================

export interface ThemeState {
  /** Whether dark mode is enabled */
  darkMode: boolean;
  /** Whether theme is being saved to server */
  isThemeSaving: boolean;
}

export interface ThemeActions {
  /** Set dark mode on/off and apply to DOM */
  setDarkMode: (enabled: boolean, persist?: boolean) => void;
  /** Toggle dark mode */
  toggleDarkMode: () => void;
  /** Load theme preference from server (requires token) */
  loadThemeFromServer: (token: string) => Promise<void>;
  /** Save theme preference to server (requires token) */
  saveThemeToServer: (token: string, darkMode: boolean) => Promise<void>;
  /** Initialize theme from localStorage (for initial load) */
  initializeTheme: () => void;
  /** Reset theme to default (light mode) - used on logout */
  resetTheme: () => void;
}

export type ThemeSlice = ThemeState & ThemeActions;

// ============================================================================
// Helper Functions
// ============================================================================

/**
 * Apply dark mode class to document
 */
function applyDarkMode(enabled: boolean): void {
  if (enabled) {
    document.documentElement.classList.add('dark');
  } else {
    document.documentElement.classList.remove('dark');
  }
}

/**
 * Load theme from localStorage
 */
function loadThemeFromStorage(): boolean {
  try {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored !== null) {
      return stored === 'true';
    }
    // Default to light mode
    return false;
  } catch {
    return false;
  }
}

/**
 * Save theme to localStorage
 */
function saveThemeToStorage(darkMode: boolean): void {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, String(darkMode));
  } catch {
    // Ignore storage errors
  }
}

/**
 * Clear theme from localStorage
 */
function clearThemeFromStorage(): void {
  try {
    localStorage.removeItem(THEME_STORAGE_KEY);
  } catch {
    // Ignore storage errors
  }
}

// ============================================================================
// Theme Slice Creator
// ============================================================================

export const createThemeSlice: StateCreator<ThemeSlice, [], [], ThemeSlice> = (set, get) => ({
  // Initial state
  darkMode: false,
  isThemeSaving: false,

  // Set dark mode (with optional storage persistence)
  setDarkMode: (enabled: boolean, persist: boolean = true) => {
    applyDarkMode(enabled);
    if (persist) {
      saveThemeToStorage(enabled);
    }
    set({ darkMode: enabled });
  },

  // Toggle dark mode
  toggleDarkMode: () => {
    const newValue = !get().darkMode;
    applyDarkMode(newValue);
    saveThemeToStorage(newValue);
    set({ darkMode: newValue });
  },

  // Load theme from server
  loadThemeFromServer: async (token: string) => {
    try {
      const response = await fetch(`${API_BASE_URL}/auth/preferences`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.ok) {
        const data = await response.json();
        if (data.success && data.data) {
          const preferences: UserPreferences = data.data;
          applyDarkMode(preferences.darkMode);
          saveThemeToStorage(preferences.darkMode);
          set({ darkMode: preferences.darkMode });
        }
      }
    } catch (error) {
      console.error('Failed to load theme from server:', error);
      // Fall back to localStorage
      const storedTheme = loadThemeFromStorage();
      applyDarkMode(storedTheme);
      set({ darkMode: storedTheme });
    }
  },

  // Save theme to server
  saveThemeToServer: async (token: string, darkMode: boolean) => {
    set({ isThemeSaving: true });
    try {
      await fetch(`${API_BASE_URL}/auth/preferences`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ darkMode }),
      });
    } catch (error) {
      console.error('Failed to save theme to server:', error);
    } finally {
      set({ isThemeSaving: false });
    }
  },

  // Initialize theme from localStorage
  initializeTheme: () => {
    const storedTheme = loadThemeFromStorage();
    applyDarkMode(storedTheme);
    set({ darkMode: storedTheme });
  },

  // Reset theme to default (light mode) - used on logout
  resetTheme: () => {
    clearThemeFromStorage();
    applyDarkMode(false);
    set({ darkMode: false });
  },
});

/**
 * Initialize theme immediately when module loads
 * Only applies stored theme if user is authenticated (has token in storage)
 * Otherwise defaults to light mode
 */
export function initializeThemeOnLoad(): void {
  // Check if user is authenticated by looking at storage
  const hasAuthInLocal = localStorage.getItem('spellion_auth');
  const hasAuthInSession = sessionStorage.getItem('spellion_auth');
  const isAuthenticated = hasAuthInLocal || hasAuthInSession;
  
  if (isAuthenticated) {
    // User is logged in - load their stored theme preference
    const storedTheme = loadThemeFromStorage();
    applyDarkMode(storedTheme);
  } else {
    // Not logged in - always use light mode
    applyDarkMode(false);
  }
}
